import React, { useState } from 'react';
import { Button } from 'antd';
import type { TableProps } from 'antd';
import { IInboundItem } from '@/interfaces/inbound';
import TableComponent from '@/components/table';
import { formatDate, formatNumber } from '@/utils/dataUtils';
import { twMerge } from 'tailwind-merge';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  getInboundStatusColor,
  getInboundStatusName,
} from '@/helpers/inbouldHelper';
import InboundProductModal from './inbound-product-modal';
import { IDataModal } from '@/interfaces/dataModal';
// import DoOrderSelector from '@/components/selector/doOrder-selector';
import { IDoOrder } from '@/interfaces/doOrder';
import { patchInboundItemUpdate } from '@/adapters/inbound';
import DoOrderSelector from '@/components/selector/doOrder-selector';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { isEmployee } from '@/utils/permissionUtils';

interface IProps {
  isView?: boolean;
  lenght?: number;
  loading: boolean;
  items: IInboundItem[];
  onOk: () => void;
}

const _inboundModal: IDataModal<IInboundItem> = {
  open: false,
};

const InboundItemTable = ({
  loading,
  items,
  onOk,
  isView = false,
  lenght,
}: IProps) => {
  dayjs.extend(relativeTime);
  const { userInfo } = useAuth();
  const [itemModal, setItemModal] =
    useState<IDataModal<IInboundItem>>(_inboundModal);

  const mutations = useMutation({
    mutationFn: async (req: Partial<IInboundItem>) =>
      await patchInboundItemUpdate(req),
    mutationKey: ['import-coa'],
    onError: error => {
      console.log('error', error);
    },
  });

  const onUpdateItem = (item: IInboundItem, doOrder?: IDoOrder) => {
    mutations.mutate({
      id: item.id,
      doOrderId: (doOrder?.id || 0) > 9999 ? 0 : doOrder?.id,
      doCode: doOrder?.code || '',
    });
  };

  const columns: TableProps<IInboundItem>['columns'] = [
    {
      title: 'INV DATE',
      dataIndex: 'invDate',
      key: 'invDate',
      align: 'center',
      render: _ => formatDate(_),
      width: 120,
    },
    {
      title: 'INV NUMBER',
      dataIndex: 'invNO',
      key: 'invNO',
      align: 'center',
      className: 'font-bold text-blue-700',
      width: 120,
    },
    {
      title: 'INCOMING CODE',
      dataIndex: 'productCode',
      key: 'productCode',
      className: 'font-bold text-blue-700',
      align: 'center',
      width: 140,
    },
    {
      title: 'LOT NUBMER',
      dataIndex: 'lotNumber',
      key: 'lotNumber',
      className: 'font-bold text-blue-700',
      align: 'center',
      width: 150,
    },
    {
      title: 'ITEM NAME',
      dataIndex: 'pdtDescriptionE',
      key: 'pdtDescriptionE',
      className: 'font-bold',
    },
    {
      title: 'จำนวน',
      dataIndex: 'totalUnit',
      key: 'totalUnit',
      className: 'font-bold',
      width: 120,
      align: 'right',
      render: (_, rec) =>
        formatNumber(
          ['KGM', 'KG'].includes(rec.baseUnitId as string)
            ? rec.totalQty
            : rec.totalUnit,
        ),
    },
    {
      title: 'หน่วย',
      dataIndex: 'uom',
      key: 'uom',
      className: 'font-bold',
      width: 80,
      align: 'center',
      render: (_, rec) => (isView ? rec.baseUnitId : _),
    },
    {
      title: 'Arrival Date',
      dataIndex: 'arrivalDate',
      key: '',
      align: 'center',
      render: _ => _ && formatDate(_),
      width: 120,
    },
    {
      title: 'Received Date',
      dataIndex: 'whReceivedDate',
      key: '',
      align: 'center',
      render: _ => _ && formatDate(_),
      width: 120,
    },
    {
      title: 'รับเข้า',
      dataIndex: 'totalReceivedQty',
      key: 'totalReceivedQty',
      className: 'font-bold',
      width: 120,
      align: 'right',
      render: (_, rec) => formatNumber(rec.totalReceivedQty),
    },
    {
      title: 'น้ำหนัก',
      dataIndex: 'totalQty',
      key: 'totalQty',
      className: 'font-bold',
      width: 120,
      align: 'right',
      render: _ => formatNumber(_),
      hidden: isView,
    },
    {
      title: 'ERROR MSG.',
      dataIndex: 'errorMessage',
      key: 'errorMessage',
      hidden: isView,
    },
    {
      title: 'สถานะ',
      dataIndex: 'status',
      key: 'status',
      className: 'font-medium',
      align: 'center',
      width: 100,
      fixed: 'right',
      render: _ => {
        const name = getInboundStatusName(_);
        const status = getInboundStatusColor(_);
        return (
          <div
            className={twMerge(
              'truncate',
              status?.bg,
              status?.text,
              'rounded py-0.5 px-1',
            )}
          >
            {name}
          </div>
        );
      },
    },
    // {
    //   title: 'DO NUMBER',
    //   dataIndex: 'doOrderId',
    //   key: 'doOrderId',
    //   className: 'font-medium',
    //   width: 140,
    //   hidden: !isView,
    //   render: (_, rec) => {
    //     return (
    //       <div>
    //         <DoOrderSelector
    //           customClass="md:min-w-[120px]"
    //           value={rec.doOrderId}
    //           size="middle"
    //           handleSelectData={function (doOrder?: IDoOrder): void {
    //             onUpdateItem(rec, doOrder);
    //           }}
    //           showAddNew={false}
    //           isRequireProduct
    //           productId={rec.productId}
    //         />
    //       </div>
    //     );
    //   },
    // },
    {
      title: '',
      dataIndex: 'id',
      key: 'action',
      className: 'font-medium',
      align: 'center',
      fixed: 'right',
      width: 60,
      hidden: isView || (!!userInfo?.bu && isEmployee()),
      render: (_, rec) => (
        <Button
          ghost
          type="primary"
          size="small"
          onClick={() =>
            setItemModal({
              open: true,
              id: rec.id,
              item: rec,
            })
          }
        >
          แก้ไข
        </Button>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <TableComponent<IInboundItem>
        columns={columns}
        criteria={undefined}
        data={{ data: items || [], totalRecords: items?.length }}
        loading={loading}
        onTableChange={undefined}
        scrollLength={lenght || 1800}
        scroll
        showPagination={false}
      />
      {itemModal?.open && (
        <InboundProductModal
          dataModal={itemModal}
          handleOk={function (): void {
            setItemModal({
              ..._inboundModal,
            });
            onOk();
          }}
          handleCancel={function (): void {
            setItemModal({
              ..._inboundModal,
            });
          }}
        />
      )}
    </div>
  );
};

export default InboundItemTable;
